import { CSSProperties } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconDrag } from '@components/icons/drag';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPinned } from '@components/icons/pinned';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

import { JobOrderingData } from 'api/jobs/requests';
import { Spacer } from 'components/Spacer';
import { useStore } from 'models/Provider';

import { PinButton } from './ordering';

type DraggableJobProps = {
  job: JobOrderingData;
  handlePin: (jobId: number, placement: 'top' | 'bottom' | 'new') => void;
  unpin: (jobId: number) => void;
  isPinned?: boolean;
  isDragging?: boolean;
};

export const DraggableJob = ({
  job,
  handlePin,
  unpin,
  isPinned = false,
  isDragging = false,
}: DraggableJobProps) => {
  const { t } = useStore();

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(job.id),
    data: {
      order: job.order,
    },
  });

  let style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  if (isDragging) {
    style.opacity = 0.65;
  }

  if (isPinned) {
    style.cursor = 'grab';
  }

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <Card space={2} interactive={isPinned ? true : false}>
        <Stack space={4} orientation="horizontal" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center" justify="flex-start">
            {isPinned ? (
              <>
                <IconDrag color="purple50" />
                <PinButton
                  tooltip={t('company.settings.joborder.unpin.hover')}
                  onClick={() => unpin(job.id)}
                />
              </>
            ) : (
              <PinButton
                isAnimated
                tooltip={t('company.settings.joborder.pin.hover')}
                onClick={() => handlePin(job.id, 'new')}
              />
            )}
            <Text font="semibold14" color="purple100" text={job.title} />
          </Stack>
          {isPinned && (
            <Menu
              buttonProps={{
                variant: 'ghost',
                iconLeft: <IconMoreVertical />,
              }}
            >
              <Menu.Item
                variant="base"
                label={t('company.settings.joborder.move.top')}
                onSelect={() => handlePin(job.id, 'top')}
              />
              <Menu.Item
                variant="base"
                label={t('company.settings.joborder.move.bottom')}
                onSelect={() => handlePin(job.id, 'bottom')}
              />
            </Menu>
          )}
        </Stack>
      </Card>
    </div>
  );
};

export const JobCard = (props: { job: JobOrderingData }) => {
  const { job } = props;
  const { setNodeRef } = useDroppable({
    id: String(job.id),
    data: {
      order: job.order,
    },
  });

  return (
    <div ref={setNodeRef}>
      <Card space={2} style={{ background: theme.color.purple10 }}>
        <Stack space={4} orientation="horizontal" justify="space-between">
          <Stack space={1} orientation="horizontal" align="center" justify="flex-start">
            <Spacer top={10} bottom={10} left={10} right={10} />
            <IconDrag color="purple50" />
            <IconButton icon={<IconPinned color="blue100" />} variant="ghost-purple" />
            <Text font="semibold14" color="purple100" text={job.title} />
          </Stack>
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconLeft: <IconMoreVertical />,
            }}
          >
            <Menu.Item variant="base" label="" />
          </Menu>
        </Stack>
      </Card>
    </div>
  );
};
